import { useState } from "react";
import { Box, Paper, Tabs, Tab, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import VideoPlayer from "./VideoPlayer";
import type { IStreamDetails, IThemeConfig } from "../types/memorial-types";
import { tokens } from "../../../../../../theme";
import ContributeChemaBar from "./ContributeChemaBar";
import ContributeChemaSheet from "./ContributeChemaSheet";
import DonationSuccessOverlay from "./DonationSuccessOverlay";

interface StreamViewerProps {
  streams: IStreamDetails[];
  activeStream: number;
  onStreamChange: (streamIndex: number) => void;
  memorialTheme: IThemeConfig;
  currentStream: IStreamDetails;
  deceasedName: string | undefined;
  hideTabs?: boolean;
  onDonationSuccess?: (donationId?: number) => void;
}

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 },
};

export default function StreamViewer({
  streams,
  activeStream,
  onStreamChange,
  memorialTheme,
  currentStream,
  deceasedName: _deceasedName,
  hideTabs,
  onDonationSuccess,
}: StreamViewerProps) {
  void _deceasedName;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [successOverlayOpen, setSuccessOverlayOpen] = useState(false);
  console.log("type of service", currentStream.booking);
  return (
    <motion.div variants={itemVariants}>
      <Paper
        sx={{
          borderRadius: "15px",
          overflow: "hidden",
          backgroundColor: "transparent",
          width: "100%",
          boxShadow: 0,
        }}
      >
        {/* Stream Tabs Bar */}
        {streams && streams.length > 1 && !hideTabs && (
          <Box
            sx={{
              backgroundColor: memorialTheme.primary,
              borderRadius: "15px 15px 0 0",
              mb: 2,
            }}
          >
            <Tabs
              value={activeStream}
              onChange={(event, newValue) => onStreamChange(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                "& .MuiTabs-scroller": {
                  // Hide scrollbar
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                },
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontSize: "1rem",
                  color: "#ffffff",
                  "&.Mui-selected": {
                    color: colors.primary[700],
                    fontWeight: "bold",
                  },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "transparent",
                },
              }}
            >
              {streams.map((stream, index) => (
                <Tab
                  key={index}
                  label={stream.title || `Stream ${index + 1}`}
                  sx={{
                    color: activeStream === index ? colors.primary[700] : colors.primary[900],
                    backgroundColor:
                      activeStream === index ? colors.primary[900] : "transparent",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      right: 0,
                      top: "25%",
                      height: "50%",
                      width: "1px",
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                    },
                    "&:last-child::after": {
                      display: "none",
                    },
                  }}
                />
              ))}
            </Tabs>
          </Box>
        )}

        {/* Main Content Area */}

        <Box sx={{ mb: 1 }}>
          {/* Main Title */}

          <Box sx={{ position: "relative" }}>
            {/* Video Player */}
            <Box
              sx={{
                borderRadius: currentStream.chema_fund_enabled
                  ? "20px 20px 0 0"
                  : "20px",
                overflow: "hidden",
                width: "100%",
                "& .video-js": {
                  borderRadius: currentStream.chema_fund_enabled
                    ? "20px 20px 0 0"
                    : "20px",
                },
              }}
            >
              <VideoPlayer
                key={currentStream.id}
                streamData={currentStream}
                borderRadius={
                  currentStream.chema_fund_enabled ? "20px 20px 0 0" : "20px"
                }
              />
            </Box>

            {currentStream.chema_fund_enabled && (
              <ContributeChemaBar onContribute={() => setSheetOpen(true)} />
            )}

            {currentStream.chema_fund_enabled && (
              <ContributeChemaSheet
                open={sheetOpen}
                onClose={() => setSheetOpen(false)}
                streamId={currentStream.id}
                streamTitle={currentStream.title}
                deceasedImage={currentStream.booking?.deceased?.[0]?.person_image}
                onDonationSuccess={(donationId) => {
                  setSheetOpen(false);
                  setSuccessOverlayOpen(true);
                  onDonationSuccess?.(donationId);
                }}
              />
            )}

            <DonationSuccessOverlay
              open={successOverlayOpen}
              onClose={() => setSuccessOverlayOpen(false)}
            />
          </Box>
        </Box>
      </Paper>
    </motion.div>
  );
}